/* eslint-disable @typescript-eslint/no-explicit-any */
import { types, Instance, flow } from 'mobx-state-tree';
import { AxiosResponse } from 'axios';
import axios from 'server';
import { GroupModel, IGroupModel, ManagerModel, IManagerModel } from './models/group.model';
import { UserGroupsView, UserGroupData } from './types';
const { model, string, array, maybeNull, reference } = types;

export const GroupTableStore = model('GroupTableStore', {
  userGroup: array(GroupModel),
  managerGroup: array(ManagerModel),
  filter: string,
  clickedGroupUser: maybeNull(reference(GroupModel)),
})
  .views((self) => ({
    get usergroupsView(): UserGroupsView[] {
      if (!self.userGroup || self.userGroup.length === 0) {
        return [];
      }
      const usersView: UserGroupsView[] = [];
      for (let i = 0; i < self.userGroup.length; i++) {
        const userGroupItem = self.userGroup[i];
        const userView: UserGroupsView = { ...userGroupItem, id: i };
        usersView.push(userView);
      }
      if (!self.filter) {
        return usersView;
      }
      const regexp = new RegExp(self.filter, 'i');
      const filteredUserGroupsView = usersView.filter(({ mail, sn, uid, givenname }) =>
        regexp.test(`${mail}${sn}${uid}${givenname}`)
      );
      return filteredUserGroupsView;
    },

    get CurrentClickedUser() {
      return self.clickedGroupUser;
    },
  }))
  .actions((self) => ({
    clearClickedUser() {
      self.clickedGroupUser = null;
    },
  }))
  .actions((self) => ({
    normalizeuserGroup({ data }: AxiosResponse): IGroupModel[] {
      self.userGroup.replace(data.group);
      return self.userGroup;
    },
    normalizemanagerGroup({ data }: AxiosResponse): IManagerModel[] {
      self.managerGroup.replace(data.group);
      return self.managerGroup;
    },
    setFilter(filter: string) {
      self.filter = filter;
    },
    setClickedUser(dn: string): void {
      self.clickedGroupUser = self.userGroup.find((user) => user.dn === dn) || null;
    },

    clearSelected(): void {
      self.clickedGroupUser = null;
    },
  }))
  .actions((self) => ({
    getUserGroup: flow(function* (dn) {
      try {
        const groupResponse = yield axios.get(`/groups/${dn}`);
        const groupData =
          groupResponse.data?.group ||
          groupResponse.data?.data?.group ||
          groupResponse.data?.data ||
          groupResponse.data;
        if (!groupData || !groupData.memberuid) {
          self.userGroup.replace([]);
          return [];
        }

        // Handle both array and single string cases
        let memberuids: string[] = [];
        if (Array.isArray(groupData.memberuid)) {
          memberuids = groupData.memberuid;
        } else if (typeof groupData.memberuid === 'string') {
          memberuids = [groupData.memberuid];
        }

        if (memberuids.length === 0) {
          self.userGroup.replace([]);
          return [];
        }
        const usersResponse = yield axios.get('/ldap/users');
        let allUsers = usersResponse.data?.data || usersResponse.data || [];
        try {
          const adminusersResponse = yield axios.get('ldap/users/admins');
          const allAdmins = adminusersResponse.data?.data || adminusersResponse.data || [];
          allUsers = [...allUsers, ...allAdmins];
        } catch (adminError: any) {
          console.warn('Could not fetch admin users (403 Forbidden):', adminError.message);
          // Continue with just regular users
        }
        const groupUsers = allUsers.filter((user: any) => {
          const userMatch = memberuids.some((memberDn: string) => {
            // Try exact match first
            const exactMatch = user.dn?.toLowerCase().trim() === memberDn.toLowerCase().trim();

            // If no exact match, try partial match by UID
            let partialMatch = false;
            if (!exactMatch) {
              const memberUid = memberDn.match(/uid=([^,]+)/i)?.[1];
              const userUid = user.dn?.match(/uid=([^,]+)/i)?.[1];
              partialMatch = memberUid && userUid && memberUid.toLowerCase() === userUid.toLowerCase();
            }
            const match = exactMatch || partialMatch;
            return match;
          });
          return userMatch;
        });
        self.userGroup.replace(groupUsers);
        return groupUsers;
      } catch (error: any) {
        console.error('Error in getUserGroup:', error);
        throw new Error(error.response?.data?.data?.message || error.message);
      }
    }),
    getManagerGroup: flow(function* (dn) {
      try {
        const response = yield axios.get(`/groups/${dn}`);
        const group = response.data.data;
        const managers = group.managers ?? [];
        self.managerGroup.replace(managers);
        return managers;
      } catch (error: any) {
        throw new Error(error.response?.data?.message || error.message);
      }
    }),
    getAppGroup: flow(function* (id) {
      try {
        const response = yield axios.get(`groups/${id}/apps`);
        const group = response.data.data;
        const apps = group.map((item: any) => item.provider);
        return apps;
      } catch (error: any) {
        throw new Error(error.response?.data?.message || error.message);
      }
    }),
    deleteGroup: flow(function* (data: UserGroupData, dn) {
      const result = yield axios
        .delete<IGroupModel>(`groups/${dn}/users`, { data })
        .then(self.clearSelected)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    deleteManager: flow(function* (data: UserGroupData) {
      try {
        const result = yield axios.delete<IGroupModel>(`groups/${data.group_id}/managers/${data.manager_id}`, {
          data: {
            manager_id: data.manager_id,
          },
        });

        self.clearSelected();
        return result;
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || error.message || 'Failed to remove manager');
      }
    }),
    deleteApp: flow(function* (data: { group_id: number; provider: string }) {
      try {
        const result = yield axios.delete(`groups/${data.group_id}/apps/${data.provider}`);

        self.clearSelected();
        return result;
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || error.message || 'Failed to remove app');
      }
    }),
  }));

export type IGroupTableStore = Instance<typeof GroupTableStore>;
